import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'

import EmployeeInitials from '@components/EmployeeInitials'
import Button from '@components/Button'
import Checkbox from '@components/Checkbox'
import AddExpertIcon from '@components/Icon/AddExpert'
import DeleteIcon from '@components/Icon/Delete'
import EditIcon from '@components/Icon/Edit'
import CopyIcon from '@components/Icon/Copy'
import OrderDown from '@components/Icon/OrderDown'
import OrderUp from '@components/Icon/OrderUp'
import { showDialog, isCancellationError } from '@components/Dialog'
import { capi } from '@atheneum/api'

class Segments extends Component {
  static propTypes = {
    segmentStore: PropTypes.object.isRequired,
    modalStore: PropTypes.object.isRequired,
    projectStore: PropTypes.object.isRequired,
    userStore: PropTypes.object.isRequired,
    growlStore: PropTypes.object.isRequired
  }

  createSegment = segment => {
    const { id, screeningQuestionsOrder, ...copiedSegment } = {
      ...segment,
      name: '',
      complete: false
    }
    this.props.modalStore.show({
      type: 'SEGMENT_FORM',
      props: {
        segment: copiedSegment,
        project: this.props.projectStore.project,
        segmentStore: this.props.segmentStore
      }
    })
  }

  editSegment = id => {
    this.props.segmentStore.get(id).then(segment => {
      this.props.modalStore.show({
        type: 'SEGMENT_FORM',
        props: {
          segment: segment,
          project: this.props.projectStore.project,
          segmentStore: this.props.segmentStore
        }
      })
    })
  }

  deleteSegment = id => {
    return showDialog({
      title: 'Delete segment',
      text: 'Are you sure you want to delete this segment?',
      type: 'warning',
      showCancelButton: true
    })
      .then(() => {
        return this.props.segmentStore
          .remove(id)
          .then(() =>
            this.props.segmentStore.getAll(this.props.projectStore.project.id)
          )
      })
      .catch(err => {
        if (isCancellationError(err)) return
        const { growlStore } = this.props
        growlStore.showError(err, `Couldn't delete the segment`)
      })
  }

  orderSegmentDown = id => {
    return this.props.segmentStore
      .orderDown(id)
      .then(() =>
        this.props.segmentStore.getAll(this.props.projectStore.project.id)
      )
  }

  orderSegmentUp = id => {
    return this.props.segmentStore
      .orderUp(id)
      .then(() =>
        this.props.segmentStore.getAll(this.props.projectStore.project.id)
      )
  }

  addEmployee = (id, projectId) => {
    const employeeId = this.props.userStore.user.id
    return this.props.segmentStore
      .addEmployee(id, employeeId)
      .then(() => this.props.segmentStore.getAll(projectId))
  }

  onChange = (segment, val) => {
    this.props.segmentStore.statusChange(segment.id, val)
  }

  render() {
    return (
      <div className='story'>
        <h2>Segments</h2>
        <div>
          {this.props.segmentStore.segments.map(segment => (
            <Segment
              key={segment.id}
              onChange={this.onChange}
              segment={segment}
              onCreate={this.createSegment}
              onEdit={this.editSegment}
              onDelete={this.deleteSegment}
              orderSegmentDown={this.orderSegmentDown}
              orderSegmentUp={this.orderSegmentUp}
              addEmployee={this.addEmployee}
              showButton={this.props.showButton}
            />
          ))}
        </div>
        {this.props.showButton && (
          <Button
            className='Segment--button'
            onClick={() => {
              this.props.modalStore.show({
                type: 'SEGMENT_FORM',
                props: {
                  segment: null,
                  project: this.props.projectStore.project,
                  segmentStore: this.props.segmentStore
                }
              })
            }}
          >
            New Segment
          </Button>
        )}
      </div>
    )
  }
}

const Segment = inject('sidebarStore')(
  observer(
    class extends Component {
      static propTypes = {
        onChange: PropTypes.func.isRequired,
        onDelete: PropTypes.func.isRequired,
        orderSegmentDown: PropTypes.func.isRequired,
        orderSegmentUp: PropTypes.func.isRequired,
        addEmployee: PropTypes.func.isRequired,
        onEdit: PropTypes.func.isRequired,
        onCreate: PropTypes.func.isRequired,
        segment: PropTypes.object.isRequired,
        sidebarStore: PropTypes.object.isRequired
      }
      constructor(props) {
        super(props)
        this.state = {
          capiSegmentStatus: undefined
        }
      }

      createSegment = () => this.props.onCreate(this.props.segment)
      editSegment = () => this.props.onEdit(this.props.segment.id)
      deleteSegment = () => this.props.onDelete(this.props.segment.id)
      orderSegmentDown = () =>
        this.props.orderSegmentDown(this.props.segment.id)
      orderSegmentUp = () => this.props.orderSegmentUp(this.props.segment.id)
      addEmployee = () =>
        this.props.addEmployee(
          this.props.segment.id,
          this.props.segment.projectId
        )
      onChange = val => this.props.onChange(this.props.segment, val)

      async componentDidMount() {
        await this.getCapiWorkstreamStatus()
      }

      async getCapiWorkstreamStatus() {
        if (this.props.segment.id) {
          const capiWorkstream = await capi.getWorkstream(this.props.segment.id)
          if (!capiWorkstream) {
            return
          }
          this.setState({
            capiSegmentStatus: capiWorkstream.live
          })
        }
      }

      formatCapiWorkstreamStatus = (live) => {
        if (typeof (live) === 'undefined') return ''
        if (live) return '- (live)'
        return '- (not live)'
      }

      render() {
        const capiWorkstreamStatus = this.formatCapiWorkstreamStatus(this.state.capiSegmentStatus)
        return (
          <div className='Segment'>
            <div className='pull-right segments__segment-wrapper'>
              {this.props.segment.employees.map(e => (
                <EmployeeInitials small employee={e} key={e.id} />
              ))}
              <span
                className='segment-edit-icon m-r-md'
                onClick={() => this.addEmployee()}
              >
                <AddExpertIcon />
              </span>
              <span
                className='segment-edit-icon m-l-md'
                onClick={() => this.orderSegmentDown()}
              >
                <OrderDown />
              </span>
              <span
                className='segment-edit-icon m-r-md'
                onClick={() => this.orderSegmentUp()}
              >
                <OrderUp />
              </span>
              {this.props.showButton && (
                <span
                  className='segment-edit-icon m-r-md'
                  onClick={() => this.createSegment()}
                >
                  <CopyIcon />
                </span>
              )}
              <span
                className='segment-edit-icon'
                onClick={() => this.editSegment()}
              >
                <EditIcon />
              </span>
              {this.props.showButton && (
                <span
                  className='segment-edit-icon'
                  onClick={() => this.deleteSegment()}
                >
                  <DeleteIcon />
                </span>
              )}
            </div>
            <Checkbox
              value={this.props.segment.complete}
              onChange={this.onChange}
            />
            <span
              className={this.props.segment.complete ? 'segment--complete' : ''}
            >
              {this.props.segment.numberOfExpert} &nbsp;
              <span
                className='segment--name'
                onClick={() =>
                  this.props.sidebarStore.openSidebar({
                    template: 'segment',
                    id: this.props.segment.id
                  })
                }
              >
                {this.props.segment.name || 'unnamed'} {capiWorkstreamStatus}
              </span>
            </span>
          </div>
        )
      }
    }
  )
)

export default inject(
  'segmentStore',
  'modalStore',
  'projectStore',
  'sidebarStore',
  'userStore',
  'growlStore'
)(observer(Segments))
